import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import config from '../../devprodConfig';
import { getKiinteistoSasToken } from '../../api/api';

const API_URL = config.apiBaseUrl;

async function fetchAttachmentBytes(att) {
  if (att.source === 'sharepoint') {
    // Try the stored pre-auth download URL first (valid ~1h from when it was fetched)
    if (att.downloadUrl) {
      try {
        const res = await fetch(att.downloadUrl);
        if (res.ok) return await res.arrayBuffer();
      } catch { /* expired — fall through to backend proxy */ }
    }
    // Fall back to backend proxy using the stable file ID
    const res = await fetch(`${API_URL}/sharepoint/download?fileId=${encodeURIComponent(att.fileId)}`, {
      credentials: 'include',
    });
    if (!res.ok) throw new Error(`SharePoint proxy returned ${res.status}`);
    return await res.arrayBuffer();
  }

  // Local Azure Blob — always get a fresh SAS URL; the stored one may have expired
  if (!att.azure_blob_name) throw new Error('No blob name for local attachment');
  const { data: tokenData } = await getKiinteistoSasToken(att.azure_blob_name);
  const url = tokenData?.sasUrl;
  if (!url) throw new Error('Failed to get SAS URL for local attachment');
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Azure blob returned ${res.status}`);
  return await res.arrayBuffer();
}

export async function mergePdfAttachments(mainPdfBytes, attachments) {
  if (!attachments?.length) return mainPdfBytes;

  let merged;
  try {
    merged = await PDFDocument.load(mainPdfBytes);
  } catch (err) {
    console.error('Failed to load main PDF for merging:', err);
    return mainPdfBytes;
  }

  const font = await merged.embedFont(StandardFonts.HelveticaBold);

  for (const att of attachments) {
    try {
      const bytes = await fetchAttachmentBytes(att);
      const donor = await PDFDocument.load(bytes);
      const pages = await merged.copyPages(donor, donor.getPageIndices());
      pages.forEach(page => {
        merged.addPage(page);
        if (att.liiteNum != null) {
          const { width, height } = page.getSize();
          const label = `Liite ${att.liiteNum}.`;
          const fontSize = 12;
          const textWidth = font.widthOfTextAtSize(label, fontSize);
          page.drawText(label, {
            x: width - textWidth - 14,
            y: height - fontSize - 10,
            size: fontSize,
            font,
            color: rgb(0.02, 0.67, 0),
          });
        }
      });
    } catch (err) {
      console.warn(`Skipping PDF attachment "${att.name}":`, err.message);
    }
  }

  return await merged.save();
}
